/**
 * Popup component
 *
 * @since 1.0.0
 */

import './style.scss';

import { __ } from '@wordpress/i18n';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';

import { usePopupSettings } from '../../../hooks';
import { CartPopupContent } from '../../cart-content';
import { CartPopupErrorBoundary } from '../../error-boundary';
import type { PopupProps } from './types';

/**
 * Popup component with Motion animations
 */
export const Popup = ({
  isVisible,
  onClose,
  title,
  content,
  cartData,
  addedProduct,
  className,
  type = 'default',
}: PopupProps) => {
  const settings = usePopupSettings();

  // Auto-hide functionality
  useEffect(() => {
    if (isVisible && settings.display.auto_hide && settings.display.auto_hide_delay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, settings.display.auto_hide_delay);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isVisible, settings.display.auto_hide, settings.display.auto_hide_delay, onClose]);

  // Check if popup is enabled
  if (!settings.general.enabled) {
    return null;
  }
  const renderContent = () => {
    if (type === 'cart' && addedProduct) {
      return (
        <CartPopupErrorBoundary>
          <CartPopupContent
            product={addedProduct}
            cartInfo={cartData?.cart_info}
            cartData={cartData}
            onClose={onClose}
            displayMode={settings.general.display_mode}
          />
        </CartPopupErrorBoundary>
      );
    }

    return content ?? __('This is a popup notification.', 'redfox-added-to-cart-popup');
  };

  const getPopupTitle = () => {
    if (type === 'cart') {
      return null; // Cart popup doesn't need a title in header
    }
    return title ?? __('Notification', 'redfox-added-to-cart-popup');
  };

  const popupClassName = `redfox-added-to-cart-popup__container ${type === 'cart' ? 'redfox-added-to-cart-popup__container--cart' : ''}`;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`redfox-added-to-cart-popup__overlay ${className ?? ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <motion.div
            className={popupClassName}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {type !== 'cart' && (
              <div className='redfox-added-to-cart-popup__header'>
                <h3 className='redfox-added-to-cart-popup__title'>{getPopupTitle()}</h3>
                <button
                  className='redfox-added-to-cart-popup__close'
                  onClick={onClose}
                  aria-label={__('Close popup', 'redfox-added-to-cart-popup')}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    width={16}
                    height={16}
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' d='M6 18 18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            )}

            {type === 'cart' && (
              <button
                className='redfox-added-to-cart-popup__close redfox-added-to-cart-popup__close--cart'
                onClick={onClose}
                aria-label={__('Close popup', 'redfox-added-to-cart-popup')}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  width={16}
                  height={16}
                >
                  <path strokeLinecap='round' strokeLinejoin='round' d='M6 18 18 6M6 6l12 12' />
                </svg>
              </button>
            )}

            <div
              className={`redfox-added-to-cart-popup__content ${type === 'cart' ? 'redfox-added-to-cart-popup__content--cart' : ''}`}
            >
              {renderContent()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
